import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { MatTableDataSource } from "@angular/material/table";
import { forkJoin, of } from "rxjs";
import { catchError } from "rxjs/operators";
import { UnixToMomentDatePipe } from 'src/app/_helpers/pipes/unix-to-moment-date.pipe';
import { IIrwinAttributes } from "src/app/_interfaces/irwin/IIrwinAttributes";
import { WfdssCase } from "src/app/_models/incident/wfdssCase/wfdssCase";
import { ConfigService } from "src/app/_services/config.service";
import { IrwinSearchService } from "src/app/_services/incident/irwin/irwin-search.service";
import { OrganizationAssessmentDataService } from "src/app/_services/organization-assessment/organization-assessment-data.service";
import { RelativeRiskDataService } from "src/app/_services/realtive-risk/relative-risk-data.service";
import { CostService } from "src/app/_services/strategy/cost.service";
import { StrategicActionService } from "src/app/_services/strategy/strategic-action.service";

interface IAttriuteValue {
  attribute: string;
  value: any;
}

@Component({
    selector: "wfdss-irwin-tool-presentation",
    templateUrl: "./irwin-tool-presentation.component.html",
    styleUrls: ["./irwin-tool-presentation.component.scss"],
    standalone: false
})
export class IrwinToolPresentationComponent implements OnInit {

  @ViewChild("rawJSONDialog") rawJSONDialog: ElementRef<HTMLDialogElement>;

  @Input() irwinData: IIrwinAttributes;
  /** Contains all Irwin Attributes, including ones that are null */
  fullIrwinData: IIrwinAttributes;

  irwinDataTabularized: MatTableDataSource<IAttriuteValue> =
    new MatTableDataSource<IAttriuteValue>();

  irwinJSONDisplayText: string;
  fullIrwinJSONDisplayText: string;

  dialogTextAreaValue: string = "";

  irwinId: string;
  @Output() irwinIdChangeEvent: EventEmitter<string> =
    new EventEmitter<string>();
  @Output() attributePushEvent: EventEmitter<{ key: string; value: string }> =
    new EventEmitter<{ key: string; value: string }>();

  @Output() attributesPushEvent: EventEmitter<{ [key in string]: string }> =
    new EventEmitter<{ [key in string]: string }>();
  @Output() fullIrwinJsonRequested = new EventEmitter<string>();
  @Output() partialIrwinJsonRequested = new EventEmitter<string>();
  
  wfdssCaseDataAttrs = [
    { key: "CalculatedAcres", name: "Calculated Acres", value: '', type: 'text'},
    { key: "EstimatedFinalCost", name: "Estimated Final Cost", value: '', type: 'text'},
    {
      key: "FireStrategyDirExtPercent",
      name: "Fire Strategy Direct Extinguishment Percent",
      validOptions:
        "Number from 0 - 100. Direct Extinguishment Percent, Indirect Confine Percent, Confine Percent, Full Supp Percent, Monitor Percent and Point Zone Percent must add up to 100%",
      value: '',
      type: 'text'
    },
    {
      key: "FireStrategyIndirConfPercent",
      name: "Fire Strategy Indirect Confine Percent",
      validOptions:
        "Number from 0 - 100. Direct Extinguishment Percent, Indirect Confine Percent, Confine Percent, Full Supp Percent, Monitor Percent and Point Zone Percent must add up to 100%",
      value: '',
      type: 'text'
    },
    {
      key: "FireStrategyConfinePercent",
      name: "Fire Strategy Confine Percent",
      validOptions:
        "Number from 0 - 100. Direct Extinguishment Percent, Indirect Confine Percent, Confine Percent, Full Supp Percent, Monitor Percent and Point Zone Percent must add up to 100%",
      value: '',
      type: 'text'
    },
    {
      key: "FireStrategyFullSuppPercent",
      name: "Fire Strategy Full Supp Percent",
      validOptions:
        "Number from 0 - 100. Direct Extinguishment Percent, Indirect Confine Percent, Confine Percent, Full Supp Percent, Monitor Percent and Point Zone Percent must add up to 100%",
      value: '',
      type: 'text'
    },
    {
      key: "FireStrategyMonitorPercent",
      name: "Fire Strategy Monitor Percent",
      validOptions:
        "Number from 0 - 100. Direct Extinguishment Percent, Indirect Confine Percent, Confine Percent, Full Supp Percent, Monitor Percent and Point Zone Percent must add up to 100%",
      value: '',
      type: 'text'
    },
    {
      key: "FireStrategyPointZonePercent",
      name: "Fire Strategy Point Zone Percent",
      validOptions:
        "Number from 0 - 100. Direct Extinguishment Percent, Indirect Confine Percent, Confine Percent, Full Supp Percent, Monitor Percent and Point Zone Percent must add up to 100%",
      value: '',
      type: 'text'
    },

    {
      key: "OrganizationalAssessment",
      name: "Organizational Assessment",
      validOptions:
        "Reference NWCG standard for valid values. (Area Command, NIMO, Type 1 Team, Type 2 Team, Type 3 Team, Type 3 IC, Type 4 IC, Type 5 IC, Complex Incident Management Team)",
      value: '',
      type: 'text'
    },
    {
      key: "RelativeRiskRating",
      name: "Relative Risk Rating",
      validOptions: "High, Moderate, Low",
      value: '',
      type: 'text'
    },
    { key: "IncidentComplexityLevel", 
      name: "Incident Complexity Level", 
      validOptions: "Type 1 Incident, Type 2 Incident, Type 3 Incident, Type 4 Incident, Type 5 Incident",
      value: '', 
      type: 'text' 
    },
    {
      key: "StrategicDecisionPublishDate",
      name: "Strategic Decision Publish Date",
      validOptions: "Numeric representation of the date. Use the date picker below to generate the value to be sent",
      value: '',
      type: 'datetime-local'
    },
    { key: "WFDSSDecisionStatus", 
      name: "WFDSS Decision Status",
      validOptions: "No Decision, Pending Decision, Approved Decision", 
      value: '', 
      type: 'text' 
    },
  ];

  loading = false;

  constructor(private costService: CostService, private strategicSvc: StrategicActionService,
              private orgAssessmentSvc: OrganizationAssessmentDataService, private relativeRiskSvc: RelativeRiskDataService,
              private irwinSearchSvc: IrwinSearchService, private environment: ConfigService) {
  }

  ngOnInit() {
      if (this.irwinData) {
          this.getData();
      }
  }

  getData() {
      let wfdssCaseId;
      let wfdssDecisionStatus;
      let currentCalculatedAcres;

      this.irwinData.IrwinID = this.irwinData.IrwinID?.replace(/\{|\}/g, ''); // IRWIN ID could come with opening and closing curly braces

      this.loading = true;
      this.irwinSearchSvc.getOrCreateWfdssCase(this.irwinData.IrwinID?.toLowerCase()).pipe(catchError(error => {
          console.log('Error occurred on API call to get or create wfdss case: ' + error);
          return of({error: true, message: error});
      })).
      subscribe(data => {
          if (!(data instanceof WfdssCase)) {
              return;
          }

          wfdssCaseId = data.wfdssCaseId;
          wfdssDecisionStatus = data.wfdssDecisionStatus;
          currentCalculatedAcres = data.currentCalculatedAcres;


          const costRequest = this.costService.getEstimatedFinalCostForWfdssCase(wfdssCaseId).pipe(catchError(error => {
              console.log('Error occurred on API call to get estimated final cost: ' + error)
              return of({error: true, message: error});
          }));
          const strategicRequest = this.strategicSvc.getReportedStrategyForWfdssCase(wfdssCaseId).pipe(catchError(error => {
              console.log('Error occurred on API call to get reported strategy: ' + error)
              return of({error: true, message: error});
          }));
          const orgAssessRequest = this.orgAssessmentSvc.getLatestOrganizationAssessmentForWfdssCaseId(wfdssCaseId).pipe(catchError(error => {
              console.log('Error occurred on API call to get organization assessment: ' + error)
              return of({error: true, message: error});
          }));
          const relativeRiskRequest = this.relativeRiskSvc.getLatestRelativeRiskForWfdssCaseId(wfdssCaseId).pipe(catchError(error => {
              console.log('Error occurred on API call to get relative risk: ' + error)
              return of({error: true, message: error});
          }));

          forkJoin([costRequest, strategicRequest,
              orgAssessRequest, relativeRiskRequest])
              .subscribe(
                  responses => {
                      const incidentCosts = responses[0] as any;
                      const reportedStrategy = responses[1] as any;
                      const orgAssessment = responses[2] as any;
                      const relativeRisk = responses[3] as any;

                      this.setValueOfWfdssDataAttrs('CalculatedAcres', currentCalculatedAcres);
                      if (incidentCosts.estimatedFinalCost) {
                          this.setValueOfWfdssDataAttrs('EstimatedFinalCost', incidentCosts.estimatedFinalCost + '');
                      }
                      if (reportedStrategy.confine) {
                          this.setValueOfWfdssDataAttrs('FireStrategyConfinePercent', reportedStrategy.confine + '');
                      }
                      if (reportedStrategy.fullSuppression) {
                          this.setValueOfWfdssDataAttrs('FireStrategyFullSuppPercent', reportedStrategy.fullSuppression + '');
                      }
                      if (reportedStrategy.monitor) {
                          this.setValueOfWfdssDataAttrs('FireStrategyMonitorPercent', reportedStrategy.monitor + '');
                      }
                      if (reportedStrategy.pointZoneProtection) {
                          this.setValueOfWfdssDataAttrs('FireStrategyPointZonePercent', reportedStrategy.pointZoneProtection + '');
                      }
                      if (orgAssessment.unitRecommendedOrg) {
                          const wfdssCodeToIrwinAcceptedValues = {
                              T1: 'Complex Incident Management Team',
                              T2: 'Complex Incident Management Team',
                              T3: 'Type 3 Team',
                              T4: 'Type 4 IC',
                              T5: 'Type 5 IC',
                          };
                          const irwinUnitRecommendedOrg = wfdssCodeToIrwinAcceptedValues[orgAssessment.unitRecommendedOrg];
                          this.setValueOfWfdssDataAttrs('OrganizationalAssessment', irwinUnitRecommendedOrg);
                      }
                      if (orgAssessment.incidentComplexityLevel) {
                        const complexityLevelWfdssCodeToIrwinAcceptedValues = {
                              T1: 'Type 1 Incident',
                              T2: 'Type 2 Incident',
                              T3: 'Type 3 Incident',
                              T4: 'Type 4 Incident',
                              T5: 'Type 5 Incident'
                        };
                        this.setValueOfWfdssDataAttrs('IncidentComplexityLevel', complexityLevelWfdssCodeToIrwinAcceptedValues[orgAssessment.incidentComplexityLevel]);
                      }
                      if (relativeRisk.relativeRiskRating) {
                          const codeToNameValues = {
                              'H': 'High',
                              'M': 'Moderate',
                              'L': 'Low'
                          }
                          const nameValue = codeToNameValues[relativeRisk.relativeRiskRating];
                          this.setValueOfWfdssDataAttrs('RelativeRiskRating', nameValue);
                      }
                      // this.setValueOfWfdssDataAttrs('StrategicDecisionPublishDate', wfdssDecisionStatus);
                      this.setValueOfWfdssDataAttrs('WFDSSDecisionStatus', wfdssDecisionStatus);
          });
      });

      // TODO: calculated acres and Strategic decision publish date
  }

  setValueOfWfdssDataAttrs(key: string, value: string) {
      const x = this.wfdssCaseDataAttrs.find(each => each.key === key);
      if(x) x.value = value;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.irwinData?.currentValue !== undefined) {
      console.debug("Returned IRWIN Data", changes.irwinData?.currentValue);
      this.irwinData = changes.irwinData?.currentValue;
      this.irwinJSONDisplayText = JSON.stringify(this.irwinData, null, 2);

      //@ts-ignore
      this.fullIrwinData = { ...this.irwinData };
      Object.keys(this.fullIrwinData).forEach((key) => {
        if (this.fullIrwinData[key] === undefined) {
          this.fullIrwinData[key] = null;
        }
      });
      this.fullIrwinJSONDisplayText = JSON.stringify(
        this.fullIrwinData,
        null,
        2
      );

      const irwinDataArray = Object.entries(this.irwinData).map(
          ([key, value]) => {
              if(key === 'StrategicDecisionPublishDate' && value) {
                  value = new UnixToMomentDatePipe().transform(value);
              }
              return {
                  attribute: key,
                  value
              };
          }
      );
      const sortedData = irwinDataArray
          .sort((a, b) => a.attribute.localeCompare(b.attribute));

      this.irwinDataTabularized.data = sortedData;

      this.getData();
    }
  }

  showIrwinModal(displayProperty) {
    const irwinId = this.irwinData?.IrwinID?.replace(/\{|\}/g, '');
    this.dialogTextAreaValue = this[displayProperty];
    this.rawJSONDialog?.nativeElement.showModal();    
    this.partialIrwinJsonRequested.emit(irwinId);
  }

  onIrwinIdChangeRequested() {
    this.irwinIdChangeEvent.emit(this.irwinId);
    this.irwinId = "";
  }

  IrwinDataTableColumnMetaData = [
    {
      columnName: "Irwin Attribute",
      propertyAccessor: (prop) => {
        return prop.attribute;
      },
    },
    {
      columnName: "Attribute Value",
      propertyAccessor: (prop) => {
        return prop.value;
      },
    },
  ];
  sortingDataAccessor = (data: IAttriuteValue, prop) => {
    switch (prop) {
      case "Irwin Attribute":
        return data?.attribute ?? "";
      case "Attribute Value":
        return data?.value ?? "";
    }
  };

  // TODO; Eventually update to onSyncIrwinAttribute
  onPushIrwinAttribute(attribute, attributeValue) {
    if (attribute === 'StrategicDecisionPublishDate') {
        const date = new Date(attributeValue);
        attributeValue = date.getTime();
    }
    this.attributePushEvent.emit({ key: attribute, value: attributeValue });
  }

  pushValues(pushEmpty: boolean) {
    // Not using Angular forms because form features not used heavilty at the moment.
    // No validation is enforced
    const attributesToSave: { [key in string]: any } = {};
    this.wfdssCaseDataAttrs.forEach((attribute) => {
      const inputElement = document.querySelector(
        `#${attribute.key}`
      ) as HTMLInputElement;
      attributesToSave[attribute.key];

      if (pushEmpty) {
          let val:any = inputElement.value;
          if (
              val === "" ||
              val === undefined
          ) {
              val = null;
          }
          if (attribute.key === 'StrategicDecisionPublishDate' && val) {
            const date = new Date(val);
            val = date.getTime();
          }
          attributesToSave[attribute.key] = val;
      } else if (
        inputElement.value !== "" &&
        inputElement.value !== null &&
        inputElement.value !== undefined
      ) {
        if (attribute.key === 'StrategicDecisionPublishDate') {
          const date = new Date(inputElement.value);
          attributesToSave[attribute.key] = date.getTime();
        } else {
          attributesToSave[attribute.key] = inputElement.value;
        }        
      }
    });

    this.attributesPushEvent.emit(attributesToSave);
  }

  getIrwinObserverURL() {
    const irwin_version = this.environment.irwin_version;

    let irwinObserverURL = 'https://irwinoat.doi.gov/observer/incidents/' + this.irwinData.IrwinID; // applicable to DEV/QA/EDU/OAT WFDSS
    switch(irwin_version) {
      case 'OAT_NEXT_IRWIN_Incidents': // use when WFDSS OAT is switched back to IRWIN OAT Next
        irwinObserverURL = 'https://irwinoat.doi.gov/observer/incidents/' + this.irwinData.IrwinID + '?v=next';
        break;
      case 'TEST_IRWIN_Incidents': // applicable to UAT WFDSS
        irwinObserverURL = 'https://irwint.doi.gov/observer/incidents/' + this.irwinData.IrwinID;
        break;
      case 'IRWIN_Incidents': // applicable to PRD WFDSS
        irwinObserverURL = "https://irwin.doi.gov/observer/incidents/" + this.irwinData.IrwinID;
        break;
    }

    return irwinObserverURL;
  }

  onInput(event: Event, attributeName): void {
    const value = (event.target as HTMLInputElement).value;
    let isValid = true;
    if (['CalculatedAcres', 'EstimatedFinalCost', 'FireStrategyConfinePercent',
      'FireStrategyFullSuppPercent', 'FireStrategyMonitorPercent', 'FireStrategyPointZonePercent'
    ].includes(attributeName)) 
    {
      const regex = /^[0-9#.]*$/;
      isValid = regex.test(value);     
    }

    if (!isValid) {
      (event.target as HTMLInputElement).value = value.slice(0, -1);
    }

  }

  onFullIrwinJsonClicked() {
    const irwinId = this.irwinData?.IrwinID?.replace(/\{|\}/g, '');
    this.dialogTextAreaValue = "Loading Full IRWIN JSON...";
    this.rawJSONDialog?.nativeElement.showModal();
    this.fullIrwinJsonRequested.emit(irwinId);
  }
}
